/**
 * NewPageOnboarding — onboarding „co dalej?" po utworzeniu strony w kreatorze /stworz.
 * Pojawia się gdy URL zawiera ?nowa=1.
 *
 * 2 stany (żeby user NIGDY nie został bez wskazówki po zamknięciu popupu):
 *  - 'modal' → duży popup z krokami + CTA do panelu
 *  - 'bar'   → trwały, nieinwazyjny pasek na dole strony (zostaje po zamknięciu modalu)
 *  - 'hidden'→ całkowicie ukryty (po zamknięciu paska)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, X, Share2, Dog, Baby, ImagePlus, LayoutGrid, Phone } from 'lucide-react';
import { toast } from 'sonner';

const MASCOT = '/mascot-user-dog.png';
const ACCENT = '#F97316';

const STEPS = {
  kennel: [
    { icon: Dog, text: 'Dodaj swoje psy — reproduktory i suki' },
    { icon: Baby, text: 'Dodaj planowane i aktualne mioty' },
    { icon: ImagePlus, text: 'Wgraj zdjęcia do galerii' },
  ],
  business: [
    { icon: LayoutGrid, text: 'Dodaj swoje usługi i cennik' },
    { icon: ImagePlus, text: 'Wgraj portfolio / zdjęcia realizacji' },
    { icon: Phone, text: 'Uzupełnij dane kontaktowe i godziny' },
  ],
};

export default function NewPageOnboarding({ kind = 'kennel' }) {
  const navigate = useNavigate();
  const isNew = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('nowa') === '1';
  const [view, setView] = useState(isNew ? 'modal' : 'hidden');

  const steps = STEPS[kind] || STEPS.kennel;
  const panelLabel = kind === 'kennel' ? 'Dodaj psy w panelu' : 'Dodaj usługi w panelu';

  const stripFlag = () => {
    try { window.history.replaceState({}, '', window.location.pathname); } catch (e) { /* ignore */ }
  };
  const minimize = () => { stripFlag(); setView('bar'); };
  const hide = () => { stripFlag(); setView('hidden'); };

  const share = async () => {
    const url = window.location.href.split('?')[0];
    try { await navigator.clipboard.writeText(url); toast.success('Link skopiowany!'); }
    catch (e) { toast.error('Nie udało się skopiować linku'); }
  };

  if (view === 'hidden') return null;

  if (view === 'bar') {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[190] w-[calc(100%-2rem)] max-w-xl" data-testid="onboarding-bar">
        <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0f1e2a]/95 backdrop-blur px-4 py-3 shadow-2xl text-white">
          <img src={MASCOT} alt="" width="36" height="36" className="w-9 h-9 flex-shrink-0" />
          <span className="text-sm font-medium flex-1 min-w-0">Twoja strona jest gotowa — dokończ ją:</span>
          <button data-testid="onboarding-bar-share" onClick={share} title="Udostępnij"
            className="hidden sm:inline-flex w-9 h-9 items-center justify-center rounded-full text-slate-300 hover:text-white hover:bg-white/10">
            <Share2 className="w-4 h-4" />
          </button>
          <button data-testid="onboarding-bar-panel" onClick={() => navigate('/panel')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-white whitespace-nowrap"
            style={{ background: ACCENT }}>
            {panelLabel} <ArrowRight className="w-4 h-4" />
          </button>
          <button data-testid="onboarding-bar-close" onClick={hide} className="text-slate-400 hover:text-white flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // view === 'modal'
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="new-page-onboarding">
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0f1e2a] p-7 text-white shadow-2xl">
        <button onClick={minimize} data-testid="onboarding-close" className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        <img src={MASCOT} alt="Asystent APIS" width="80" height="80" className="w-20 h-20 mx-auto mb-3" />
        <h2 className="text-2xl font-extrabold text-center mb-1">Twoja strona jest online! 🎉</h2>
        <p className="text-sm text-slate-300 text-center mb-6">Świetna robota. Oto co warto zrobić teraz, żeby ją dokończyć:</p>
        <ul className="space-y-3 mb-6">
          {steps.map((s, i) => (
            <li key={i} className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(249,115,22,0.15)' }}>
                <s.icon className="w-5 h-5" style={{ color: ACCENT }} />
              </span>
              <span className="text-sm text-slate-200">{s.text}</span>
            </li>
          ))}
        </ul>
        <button data-testid="onboarding-go-panel" onClick={() => navigate('/panel')}
          className="w-full h-12 rounded-full font-bold text-white inline-flex items-center justify-center gap-2 mb-3 transition-transform hover:-translate-y-0.5"
          style={{ background: ACCENT, boxShadow: '0 8px 24px rgba(249,115,22,0.35)' }}>
          Przejdź do panelu {kind === 'kennel' ? 'i dodaj psy' : 'i dodaj usługi'} <ArrowRight className="w-4 h-4" />
        </button>
        <div className="flex items-center justify-center gap-4 text-sm">
          <button data-testid="onboarding-stay" onClick={minimize} className="text-slate-400 hover:text-white">Zobacz stronę</button>
          <span className="text-slate-600">·</span>
          <button data-testid="onboarding-share" onClick={share} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white">
            <Share2 className="w-4 h-4" /> Udostępnij
          </button>
        </div>
      </div>
    </div>
  );
}
